<script>
import talkizyLogo from '@app/assets/img/talkizy-logo.png';

// Présentation partagée de Talkizy (logo, explication, atouts) utilisée par la
// page de connexion (owner) ET par l'invitation à rejoindre l'équipe (admin) :
// seuls le titre/texte du hero et la zone d'actions (slots) diffèrent.
export default {
  name: 'TalkizyIntro',
  props: {
    title: {
      type: String,
      default: 'Organisez des classes virtuelles depuis Teachizy',
    },
    text: {
      type: String,
      default: 'Connectez gratuitement votre compte Talkizy pour organiser et gérer vos sessions'
        + ' de visioconférence avec vos apprenants, sans quitter votre espace formateur.',
    },
  },
  data: () => ({ talkizyLogo }),
};
</script>

<template>
  <div class="talkizyintro">

    <div class="talkizyintro_hero has-text-centered">
      <img
        :src="talkizyLogo"
        alt="Talkizy"
        class="talkizyintro_logo mb-6"
      >
      <h1 class="title is-4 mb-3">
        {{ title }}
      </h1>
      <p class="talkizyintro_hero_text mx-auto mb-4 has-text-grey-dark">
        {{ text }}
      </p>
      <p class="talkizyintro_hero_note mx-auto mb-6 is-size-8 has-text-grey-dark">
        <b-icon icon="info-circle" size="is-small" type="is-primary" />
        Talkizy est notre outil de visioconférence, conçu par l'équipe Teachizy.
        Son utilisation nécessite un compte Talkizy dédié, distinct de votre compte Teachizy.
      </p>
      <slot name="hero-actions" />
    </div>

    <div class="talkizyintro_pitch has-text-centered mb-5">
      <h2 class="title is-4 mb-2">
        Bien plus qu'une simple visioconférence
      </h2>
      <p class="talkizyintro_pitch_text mx-auto has-text-grey-dark">
        Talkizy réunit tout ce dont vous avez besoin pour animer,
        sécuriser et valoriser ses classes virtuelles. Pensé pour la formation,
        développé par l'équipe Teachizy et hébergé en France.
      </p>
    </div>

    <div class="columns is-multiline">
      <div class="column is-4">
        <div class="talkizyintro_feature box has-text-centered p-6">
          <span class="talkizyintro_feature_icon mb-4">
            <b-icon icon="video" type="is-primary" />
          </span>
          <h3 class="has-text-weight-bold mb-2">
            Sessions en direct
          </h3>
          <p class="is-size-8 has-text-grey-dark">
            Planifiez et lancez vos classes virtuelles en quelques clics,
            avec ou sans inscription préalable.
          </p>
        </div>
      </div>
      <div class="column is-4">
        <div class="talkizyintro_feature box has-text-centered p-6">
          <span class="talkizyintro_feature_icon mb-4">
            <b-icon icon="users" type="is-primary" />
          </span>
          <h3 class="has-text-weight-bold mb-2">
            Vos apprenants réunis
          </h3>
          <p class="is-size-8 has-text-grey-dark">
            Invitez automatiquement les apprenants inscrits
            à vos formations, sans ressaisie.
          </p>
        </div>
      </div>
      <div class="column is-4">
        <div class="talkizyintro_feature box has-text-centered p-6">
          <span class="talkizyintro_feature_icon mb-4">
            <b-icon icon="signature" type="is-primary" />
          </span>
          <h3 class="has-text-weight-bold mb-2">
            Émargement &amp; Qualiopi
          </h3>
          <p class="is-size-8 has-text-grey-dark">
            Feuilles d'émargement signées par les participants et enquêtes
            de satisfaction, pour une conformité Qualiopi sans effort.
          </p>
        </div>
      </div>
      <div class="column is-4">
        <div class="talkizyintro_feature box has-text-centered p-6">
          <span class="talkizyintro_feature_icon mb-4">
            <b-icon icon="robot" type="is-primary" />
          </span>
          <h3 class="has-text-weight-bold mb-2">
            Résumé &amp; transcription IA
          </h3>
          <p class="is-size-8 has-text-grey-dark">
            Transcription automatique et résumé généré par IA à l'issue
            de chaque session, pour ne rien perdre de vos échanges.
          </p>
        </div>
      </div>
      <div class="column is-4">
        <div class="talkizyintro_feature box has-text-centered p-6">
          <span class="talkizyintro_feature_icon mb-4">
            <b-icon icon="shield-alt" type="is-primary" />
          </span>
          <h3 class="has-text-weight-bold mb-2">
            Hébergement 100 % français
          </h3>
          <p class="is-size-8 has-text-grey-dark">
            Vos sessions, données et enregistrements sont hébergés en France,
            dans le strict respect du RGPD.
          </p>
        </div>
      </div>
      <div class="column is-4">
        <div class="talkizyintro_feature box has-text-centered p-6">
          <span class="talkizyintro_feature_icon mb-4">
            <b-icon icon="calendar-alt" type="is-primary" />
          </span>
          <h3 class="has-text-weight-bold mb-2">
            Tout au même endroit
          </h3>
          <p class="is-size-8 has-text-grey-dark">
            Retrouvez l'historique de vos sessions, les statistiques et le suivi
            de vos apprenants directement dans Teachizy.
          </p>
        </div>
      </div>
    </div>

    <div v-if="$slots['footer-actions']" class="has-text-centered mt-12">
      <slot name="footer-actions" />
    </div>

  </div>
</template>

<style lang="scss" scoped>
.talkizyintro {
  max-width: 920px;
  margin: 0 auto;

  &_hero {
    position: relative;
    overflow: hidden;
    padding: pxToRem(46) pxToRem(24) pxToRem(40);
    border: 1px solid $theme_color_grey_light;
    border-radius: $radius;
    background: $theme_color_grey_light2;

    // soft glow échos du cyan Talkizy et du turquoise Teachizy
    &::before,
    &::after {
      content: '';
      position: absolute;
      border-radius: 50%;
      pointer-events: none;
    }

    &::before {
      top: -140px;
      left: -80px;
      width: 380px;
      height: 380px;
      background: radial-gradient(closest-side, rgba(11, 197, 250, 0.22), transparent);
    }

    &::after {
      bottom: -160px;
      right: -100px;
      width: 420px;
      height: 420px;
      background: radial-gradient(closest-side, rgba(50, 196, 192, 0.24), transparent);
    }

    > * {
      position: relative;
      z-index: 1;
    }

    &_text {
      max-width: 520px;
    }

    &_note {
      max-width: 520px;

      .icon {
        vertical-align: middle;
      }
    }
  }

  &_logo {
    max-height: 40px;
  }

  &_pitch {
    margin-top: pxToRem(48);

    &_text {
      max-width: 620px;
    }
  }

  &_feature {
    height: 100%;
    transition: transform 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 6px 16px 0 rgba(128, 128, 128, 0.3);
    }

    &_icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: $theme_color_primary_light;
    }
  }
}
</style>
